// ------------------------------------------------------------------------------
// name: useSafeMutation
// author: mudas
// created: 2025/8/4
// ------------------------------------------------------------------------------

import { isSuccessCode } from 'common/service';
import { useMutation } from '@tanstack/react-query';

import type { MutateOptions } from '@tanstack/query-core/build/lib/types';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query/build/lib/types';

export interface MutationResult<TData, TError, TVariables, TContext>
  extends Omit<UseMutationResult<TData, TError, TVariables, TContext>, 'mutate'> {
  mutate: (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => Promise<TData>;
}

/**
 * 接口请求代理
 * @param options
 */
export const useSafeMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): MutationResult<TData, TError, TVariables, TContext> => {
  const { ...mutationOptions } = options;
  const mutation = useMutation<ExtendAxioResponse<TData>, TError, TVariables, TContext>(mutationOptions);

  const safeMutate = (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => {
    return new Promise<TData>((resolve, reject) => {
      mutation.mutate(variables, {
        ...options,
        onSuccess: (res, variables, context) => {
          if (!isSuccessCode(res?.code)) {
            reject(new Error(res?.message || res?.msg || '操作失败'));
          } else {
            resolve(res.data);
          }
          options?.onSuccess?.(res.data, variables, context);
        },
        onError: (error, variables, context) => {
          reject(error);
          options?.onError?.(error, variables, context);
        },
      });
    });
  };

  return {
    ...mutation,
    mutate: safeMutate,
  };
};
